import { c as _c } from "react/compiler-runtime";
import { useQuery } from "react-query";
import { useApi } from "./useApi";
import { useOrgFromPathname } from "./useOrgFromPathname";
import { useConnectedGithubOrgsNonSuspense } from "./useConnectedGithubOrgs";
import type { SessionMode } from "@/components/codesmith/types";
import { assistantSessionsPath } from "@/lib/assistant-sessions";
import type { NamedOrg } from "@/lib/named-org";
export interface CachedAssistantSession {
  id: string;
  user_id: number | null;
  user_name: string | null;
  title: string | null;
  status: string;
  source: string;
  total_messages: number;
  created_at: string;
  updated_at: string;
  last_message_at: string | null;
  last_user_message_at: string | null;
  mode: SessionMode;
  pinned?: boolean;
  managed_prs?: Array<{
    pr_number: number;
    status: "open" | "draft" | "merged" | "closed";
    repository: string;
    title?: string | null;
    head_branch?: string | null;
  }>;
}
export interface SearchedSession extends CachedAssistantSession {
  isMine: boolean;
  /** Transcript text that matched a content search. */
  snippet?: string;
}
const SESSION_CACHE_LIMIT = 100;
const STALE_TIME = 5 * 60 * 1000; // 5 minutes
const MAX_RECENT_SESSIONS = 3;
export function useAssistantSessionCache() {
  const $ = _c(19);
  const api = useApi();
  const activeGithubOrg = useOrgFromPathname();
  const {
    connectedGithubOrgs
  } = useConnectedGithubOrgsNonSuspense();
  let t0;
  if ($[0] !== activeGithubOrg || $[1] !== connectedGithubOrgs?.installations) {
    t0 = connectedGithubOrgs?.installations.find(installation => installation?.account?.login === activeGithubOrg);
    $[0] = activeGithubOrg;
    $[1] = connectedGithubOrgs?.installations;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const installation_0 = t0;
  const codesmithEnabled = installation_0?.codesmith_enabled ?? false;
  let t1;
  if ($[3] !== activeGithubOrg || $[4] !== installation_0) {
    t1 = activeGithubOrg && installation_0 ? {
      orgName: activeGithubOrg,
      instanceSlug: installation_0.instance_slug
    } : null;
    $[3] = activeGithubOrg;
    $[4] = installation_0;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const namedOrg = t1;
  const t2 = namedOrg?.instanceSlug;
  let t3;
  if ($[6] !== activeGithubOrg || $[7] !== t2) {
    t3 = ["assistantSessionCache", t2, activeGithubOrg];
    $[6] = activeGithubOrg;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== api || $[10] !== namedOrg) {
    t4 = async () => {
      const response = await api.get(`${assistantSessionsPath(namedOrg)}?status=active&limit=${SESSION_CACHE_LIMIT}`);
      return (response.data.sessions || []) as CachedAssistantSession[];
    };
    $[9] = api;
    $[10] = namedOrg;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const t5 = !!namedOrg && codesmithEnabled;
  let t6;
  if ($[12] !== t5) {
    t6 = {
      enabled: t5,
      staleTime: STALE_TIME,
      cacheTime: STALE_TIME * 2,
      refetchOnWindowFocus: false
    };
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const {
    data,
    isLoading
  } = useQuery(t3, t4, t6);
  let t7;
  if ($[14] !== data) {
    t7 = data || [];
    $[14] = data;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== isLoading || $[17] !== t7) {
    t8 = {
      sessions: t7,
      isLoading
    };
    $[16] = isLoading;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  return t8;
}
function sessionRecencyMs(session: CachedAssistantSession): number {
  return new Date(session.last_message_at || session.updated_at).getTime();
}

// Most recently active sessions, with the user's own sessions first.
// Used to surface recent Codesmith sessions in the command palette before
// the user types a search query.
export function getRecentSessions(sessions: CachedAssistantSession[], limit?: number, opts?: {
  userId?: number;
}): SearchedSession[] {
  const effectiveLimit = limit ?? MAX_RECENT_SESSIONS;
  const userId = opts?.userId;
  const active = sessions.filter(session => session.status === "active").map(session => ({
    ...session,
    isMine: userId != null && session.user_id === userId
  })).sort((a, b) => sessionRecencyMs(b) - sessionRecencyMs(a));
  const mine = active.filter(session => session.isMine);
  const team = active.filter(session => !session.isMine);
  return [...mine, ...team].slice(0, effectiveLimit);
}